import React, { Dispatch, SetStateAction } from 'react';
import { DonorBasicInfo } from '../../models/DonorBasicInfo';
import { AREA_TEXTS } from '../../consts';

type BasicInfoProps = {
  userNo: number;
  donorName: string;
  setDonorName: Dispatch<SetStateAction<string>>;
  basicInfo: DonorBasicInfo | null;
  setBasicInfo: Dispatch<SetStateAction<DonorBasicInfo | null>>;
  kycCertified: boolean;
}

const BasicInfo = (props: BasicInfoProps) => {
  const formatDonorName = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value;
    props.setDonorName(val.slice(0,2).toUpperCase());
  }

  return (
    <>
      <h3 className="sub_title">基本情報</h3>
      <div className="form">
        <dl className="graf_list">
          <dt>ドナーNo.</dt>
          <dd>{ props.userNo }</dd>
          <dt>イニシャル</dt>
          <dd>
            <input
              type="text"
              value={ props.donorName }
              maxLength={ 2 }
              onChange={ e => props.setDonorName(e.target.value) }
              onBlur={ e => formatDonorName(e) }
              placeholder="TB"
            />
          </dd>
          <dt>生年月日</dt>
          <dd>
            { props.kycCertified ? (
              props.basicInfo?.birthDate.replace(/-/g, '/') || '－'
            ) : (
              <input
                type="date"
                value={ props.basicInfo?.birthDate || '' }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("birthDate", e.target.value) : null) }
              />
            )}
          </dd>
          <dt>エリア</dt>
          <dd>
            <div className="flex row">
              <span className="wpcf7-form-control-wrap checkbox-content">
                <span className="wpcf7-form-control wpcf7-checkbox wpcf7-validates-as-required">
                  { Object.entries(AREA_TEXTS).map(([code, name], index) => (
                    <span key={ code } className={ `wpcf7-list-item ${ index === 0 ? 'first' : '' }` }>
                      <label>
                        <input
                          type="checkbox"
                          name="checkbox-content[]"
                          value={ name }
                          checked={ props.basicInfo?.areas?.includes(code) || false }
                          onChange={ e => {
                            const isChecked = e.target.checked;
                            props.setBasicInfo(prev => {
                              if (!prev) return prev;
                              const currentAreas = prev.areas || [];
                              const newAreas = isChecked
                                ? [...currentAreas, code]
                                : currentAreas.filter(a => a !== code);
                              return prev.set('areas', newAreas);
                            });
                          }}
                        />
                        <span className="wpcf7-list-item-label">{name}</span>
                      </label>
                    </span>
                  ))}
                </span>
              </span>
            </div>
            <p className="area-hint">※10件以内をお勧めします。11件以上選択すると、エリア指定の検索結果から除外されます。</p>
          </dd>
          <dt>最終学歴</dt>
          <dd className="flex">
            <div className="select select01">
              <select
                value={ props.basicInfo?.collegeType || '' }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("collegeType", e.target.value) : null) }
              >
                <option value="">選択してください</option>
                <option value="PUBLIC">国公立大学</option>
                <option value="PRIVATE">私立大学</option>
                <option value="HIGHSCHOOL">高等学校</option>
                <option value="OTHER">その他</option>
              </select>
            </div>
            <div className="select select01">
              <select
                value={ props.basicInfo?.majorType || '' }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("majorType", e.target.value) : null) }
              >
                <option value="">選択してください</option>
                <option value="BS">理系</option>
                <option value="BA">文系</option>
                <option value="OTHER">その他</option>
              </select>
            </div>
          </dd>
          <dt>大学・高校名</dt>
          <dd>
            <input
              type="text"
              placeholder="〇〇大学"
              value={ props.basicInfo?.college || '' }
              onChange={ e => props.setBasicInfo(prev => prev ? prev.set("college", e.target.value) : null) }
            />
          </dd>
          <dt>学部・学科名</dt>
          <dd>
            <input
              type="text"
              placeholder="△△学部"
              value={ props.basicInfo?.major || '' }
              onChange={ e => props.setBasicInfo(prev => prev ? prev.set("major", e.target.value) : null) }
            />
          </dd>
          <dt>婚姻歴</dt>
          <dd className="flex">
            <div className="select select01">
              <select
                value={ props.basicInfo?.marriage || '' }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("marriage", e.target.value) : null) }
              >
                <option value="">選択してください</option>
                <option value="UNMARRIED">未婚</option>
                <option value="MARRIED">既婚</option>
                <option value="DIVORCED">離婚</option>
              </select>
            </div>
          </dd>
          <dt>血縁関係のある子ども</dt>
          <dd className="flex">
            <span>男</span>
            <div className="select select01">
              <select
                value={ props.basicInfo?.son || 0 }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("son", Number(e.target.value)): null) }
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5以上</option>
              </select>
            </div>
            <span>女</span>
            <div className="select select01">
              <select
                value={ props.basicInfo?.daughter || 0 }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("daughter", Number(e.target.value)) : null) }
              >
                <option value="">選択してください</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5以上</option>
              </select>
            </div>
          </dd>
          <dt>国籍</dt>
          <dd>
            <div className="select select01">
              <select
                value={ props.basicInfo?.nationality || '' }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("nationality", e.target.value) : null) }
              >
                <option value="">選択してください</option>
                <option value="JAPANESE">日本</option>
                <option value="OTHER">日本以外</option>
              </select>
            </div>
          </dd>
          <dt>人種</dt>
          <dd>
            <div className="select select01">
              <select
                value={ props.basicInfo?.race || '' }
                onChange={ e => props.setBasicInfo(prev => prev ? prev.set("race", e.target.value) : null) }
              >
                <option value="">選択してください</option>
                <option value="JAPANESE">日本人</option>
                <option value="EAST_ASIAN">東アジア</option>
                <option value="OTHER_ASIAN">他のアジア</option>
                <option value="CAUCASIAN">白人系</option>
                <option value="AFRICAN">アフリカ系</option>
                <option value="HISPANIC">ヒスパニック系</option>
                <option value="MIXED">混血</option>
                <option value="OTHER">その他</option>
              </select>
            </div>
          </dd>
          <dt>自己PR</dt>
          <dd>
            <textarea
              value={ props.basicInfo?.comments || '' }
              onChange={ e => props.setBasicInfo(prev => prev ? prev.set("comments", e.target.value) : null) }
              cols={ 30 }
              rows={ 10 }
            />
          </dd>
        </dl>
      </div>
    </>
  )
}

export default BasicInfo;
